import { useState, type CSSProperties } from 'react';
import { X } from 'lucide-react';
import { createPortal } from 'react-dom';
import SaveAnimatedIcon from '@/components/ui/SaveAnimatedIcon';
import UndoAnimatedIcon from '@/components/ui/UndoAnimatedIcon';
import { createWebhook } from './webhooks.service';
import { WEBHOOK_EVENTS, type CreatedWebhook, type WebhookEvent } from './types';

const EVENT_GROUPS: Array<{ label: string; events: WebhookEvent[] }> = [
  { label: 'Pessoas', events: ['pessoa.created', 'pessoa.updated', 'pessoa.deleted'] },
  { label: 'Obras', events: ['obra.created', 'obra.updated', 'obra.status_changed', 'obra.deleted'] },
  { label: 'Atividades', events: ['atividade.created', 'atividade.updated', 'atividade.completed', 'atividade.deleted'] },
  { label: 'OS', events: ['os.created', 'os.updated', 'os.signed', 'os.deleted'] },
];

const BACKDROP_STYLE: CSSProperties = {
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(0,0,0,0.45)',
  zIndex: 9900,
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'flex-end',
};

const PANEL_STYLE: CSSProperties = {
  width: '480px',
  maxWidth: '100vw',
  height: '100vh',
  background: 'var(--panel-bg, #fff)',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '-4px 0 24px rgba(0,0,0,0.12)',
  overflowY: 'auto',
};

const HEADER_STYLE: CSSProperties = {
  padding: '20px 24px 16px',
  borderBottom: '1px solid var(--panel-border, #e5e7eb)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexShrink: 0,
  background: '#f8faff',
};

const BODY_STYLE: CSSProperties = {
  padding: '24px',
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
};

const FOOTER_STYLE: CSSProperties = {
  padding: '16px 24px',
  borderTop: '1px solid var(--panel-border, #e5e7eb)',
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '8px',
  flexShrink: 0,
  background: '#f9fafb',
};

const LABEL_STYLE: CSSProperties = {
  display: 'block',
  fontSize: '12px',
  fontWeight: 600,
  color: '#6b7280',
  textTransform: 'uppercase',
  letterSpacing: '0.4px',
  marginBottom: '6px',
};

const INPUT_STYLE: CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '9px 12px',
  border: '1px solid #e5e7eb',
  borderRadius: '6px',
  fontSize: '14px',
  outline: 'none',
  background: 'white',
};

export interface CreateWebhookDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (result: CreatedWebhook) => void;
}

export default function CreateWebhookDrawer({
  isOpen,
  onClose,
  onCreated,
}: CreateWebhookDrawerProps) {
  const [url, setUrl] = useState('');
  const [selected, setSelected] = useState<Set<WebhookEvent>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const isUrlValid = url.trim().startsWith('https://');
  const isDisabled = submitting || !isUrlValid || selected.size === 0;

  const toggle = (ev: WebhookEvent) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(ev)) next.delete(ev); else next.add(ev);
      return next;
    });
  };

  const toggleGroup = (events: WebhookEvent[]) => {
    setSelected((prev) => {
      const next = new Set(prev);
      const allSelected = events.every((e) => next.has(e));
      if (allSelected) events.forEach((e) => next.delete(e));
      else events.forEach((e) => next.add(e));
      return next;
    });
  };

  const handleSubmit = async () => {
    if (isDisabled) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await createWebhook({
        url: url.trim(),
        events: Array.from(selected) as WebhookEvent[],
      });
      setUrl('');
      setSelected(new Set());
      onCreated(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao criar webhook.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (submitting) return;
    setUrl('');
    setSelected(new Set());
    setError(null);
    onClose();
  };

  return createPortal(
    <div style={BACKDROP_STYLE} onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}>
      <div style={PANEL_STYLE} role="dialog" aria-modal="true" aria-label="Novo webhook">

        <div style={HEADER_STYLE}>
          <div>
            <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#154680' }}>
              Novo webhook
            </h2>
            <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#6b7280' }}>
              Receba notificações em tempo real quando dados mudam
            </p>
          </div>
          <button
            onClick={handleClose}
            disabled={submitting}
            style={{
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              color: '#6b7280',
              borderRadius: '4px',
            }}
            aria-label="Fechar"
          >
            <X size={20} />
          </button>
        </div>

        <div style={BODY_STYLE}>

          {/* URL */}
          <div>
            <label htmlFor="webhook-url" style={LABEL_STYLE}>
              URL de destino <span style={{ color: '#dc2626' }}>*</span>
            </label>
            <input
              id="webhook-url"
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://exemplo.com/webhooks/joshua"
              style={{
                ...INPUT_STYLE,
                borderColor: url && !isUrlValid ? '#fca5a5' : '#e5e7eb',
              }}
              disabled={submitting}
              autoFocus
            />
            <p style={{ margin: '4px 0 0', fontSize: '11px', color: '#9ca3af' }}>
              Endpoint HTTPS público que receberá os eventos via POST com assinatura HMAC-SHA256.
            </p>
            {url && !isUrlValid && (
              <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#dc2626' }}>
                A URL precisa começar com <code>https://</code>.
              </p>
            )}
          </div>

          {/* Events */}
          <div>
            <label style={LABEL_STYLE}>
              Eventos <span style={{ color: '#dc2626' }}>*</span>
              {selected.size > 0 && (
                <span style={{ marginLeft: '8px', fontWeight: 400, color: '#9ca3af' }}>
                  ({selected.size} selecionado{selected.size !== 1 ? 's' : ''})
                </span>
              )}
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {EVENT_GROUPS.map((group) => {
                const allSelected = group.events.every((e) => selected.has(e));
                const someSelected = group.events.some((e) => selected.has(e));
                return (
                  <div
                    key={group.label}
                    style={{
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      overflow: 'hidden',
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => toggleGroup(group.events)}
                      disabled={submitting}
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '8px 12px',
                        background: allSelected ? '#eff6ff' : '#f9fafb',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: 600,
                        color: allSelected ? '#154680' : '#374151',
                        textAlign: 'left',
                      }}
                    >
                      <span>{group.label}</span>
                      <span style={{ fontSize: '11px', fontWeight: 500, color: '#6b7280' }}>
                        {someSelected ? `${group.events.filter((e) => selected.has(e)).length}/${group.events.length}` : 'nenhum'}
                      </span>
                    </button>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', padding: '10px 12px' }}>
                      {group.events.map((ev) => {
                        const checked = selected.has(ev);
                        return (
                          <label
                            key={ev}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              padding: '4px 10px',
                              border: `1.5px solid ${checked ? '#154680' : '#e5e7eb'}`,
                              borderRadius: '999px',
                              cursor: 'pointer',
                              background: checked ? '#eff6ff' : 'white',
                              fontSize: '12px',
                              fontFamily: 'monospace',
                              color: checked ? '#154680' : '#6b7280',
                              transition: 'all 0.15s',
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggle(ev)}
                              disabled={submitting}
                              style={{ accentColor: '#154680', cursor: 'pointer', margin: 0 }}
                              aria-label={ev}
                            />
                            {ev}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Error */}
          {error && (
            <div style={{
              padding: '10px 12px',
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '6px',
              color: '#dc2626',
              fontSize: '13px',
            }}>
              {error}
            </div>
          )}
        </div>

        <div style={FOOTER_STYLE}>
          <button
            className="btn-add undo-animated"
            onClick={handleClose}
            disabled={submitting}
          >
            <UndoAnimatedIcon size={15} />
            <span className="btn-text">Cancelar</span>
          </button>
          <button
            className="btn-add save-animated"
            onClick={handleSubmit}
            disabled={isDisabled}
          >
            {submitting ? (
              <>
                <div style={{
                  width: '14px',
                  height: '14px',
                  border: '2px solid rgba(21,70,128,0.3)',
                  borderTop: '2px solid #154680',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  flexShrink: 0,
                }} />
                <span className="btn-text">Criando...</span>
              </>
            ) : (
              <>
                <SaveAnimatedIcon size={16} />
                <span className="btn-text">Criar webhook</span>
              </>
            )}
          </button>
        </div>

      </div>
    </div>,
    document.body
  );
}
